import { IText, util } from 'fabric';

type FabricObjectWithDotsEffect = IText & {
    isDotsEffect?: boolean;
    _render_original_dots?: (ctx: CanvasRenderingContext2D) => void;
    _offscreenCanvas_dots?: HTMLCanvasElement;
    _textureDirty_dots?: boolean;
    set_original_dots?(key: any, value?: any): FabricObjectWithDotsEffect;
};

function customDotsRender(this: FabricObjectWithDotsEffect, ctx: CanvasRenderingContext2D) {
    if (!this.isDotsEffect) {
        this._render_original_dots?.(ctx);
        return;
    }

    if (this._textureDirty_dots || !this._offscreenCanvas_dots) {
        const offscreen = util.createCanvasElement();
        const zoom = 2; // Use supersampling for better quality
        offscreen.width = this.width * zoom;
        offscreen.height = this.height * zoom;
        const octx = offscreen.getContext('2d')!;
        
        octx.scale(zoom, zoom);
        octx.translate(this.width / 2, this.height / 2);
        this._render_original_dots?.(octx);
        
        this._offscreenCanvas_dots = offscreen;
        this._textureDirty_dots = false;
    }

    const offscreen = this._offscreenCanvas_dots;
    if (!offscreen) return;
    
    const octx = offscreen.getContext('2d')!;
    const panelW = offscreen.width;
    const panelH = offscreen.height;
    
    const cols = 50;
    const rows = Math.ceil(cols * (panelH / panelW));
    const dotSize = 1.25;
    
    ctx.save();
    
    const imageData = octx.getImageData(0, 0, panelW, panelH).data;

    for (let iy = 0; iy < rows; iy++) {
        for (let ix = 0; ix < cols; ix++) {
            const tx = Math.floor((ix / (cols - 1)) * panelW);
            const ty = Math.floor((iy / (rows - 1)) * panelH);
            
            const alphaIndex = (ty * panelW + tx) * 4 + 3;
            const alpha = imageData[alphaIndex] / 255;

            if (alpha < 0.5) continue;

            const x = (ix / (cols - 1) - 0.5) * this.width;
            const y = (iy / (rows - 1) - 0.5) * this.height;
            const z = 0;

            // Isometric projection
            const sx = (x - y) * 0.9;
            const sy = (x + y) * 0.5 - z;
            
            ctx.fillStyle = this.fill as string;
            ctx.fillRect(sx, sy, dotSize, dotSize);
        }
    }
    ctx.restore();
}

export function enableDotsEffect(textObject: IText, dotSize: number = 3) {
    const obj = textObject as FabricObjectWithDotsEffect;

    if (!obj._render_original_dots) {
        obj._render_original_dots = obj._render.bind(obj);
        obj._render = customDotsRender as any;

        obj.set_original_dots = obj.set.bind(obj);
        obj.set = function(this: FabricObjectWithDotsEffect, key: any, value?: any) {
            const watchedProps = ['text', 'fontSize', 'fontFamily', 'fontWeight', 'fill', 'stroke', 'strokeWidth', 'textBackgroundColor', 'scaleX', 'scaleY'];
            let dirty = false;
            if (typeof key === 'object') {
                for (const k in key) if (watchedProps.includes(k)) { dirty = true; break; }
            } else if (watchedProps.includes(key)) {
                dirty = true;
            }
            if (dirty) this._textureDirty_dots = true;
            this.set_original_dots?.(key, value);
            return this;
        }.bind(obj);
    }
    
    obj.isDotsEffect = true;
    obj._textureDirty_dots = true;
    obj.objectCaching = false;
    obj.dirty = true;
    obj.canvas?.requestRenderAll();
}

export function disableDotsEffect(textObject: IText) {
    const obj = textObject as FabricObjectWithDotsEffect;

    if (obj._render_original_dots) {
        obj._render = obj._render_original_dots;
        delete obj._render_original_dots;
    }
    if (obj.set_original_dots) {
        obj.set = obj.set_original_dots;
        delete obj.set_original_dots;
    }

    obj.isDotsEffect = false;
    delete obj._offscreenCanvas_dots;
    obj.objectCaching = true;
    obj.dirty = true;
    obj.canvas?.requestRenderAll();
}
